import {Box} from '@mui/material';
import {t} from 'i18next';
import {noop, orderBy} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import AurDrawerPanelContainer from '../../../client-details/AurDrawerPanelContainer';
import AurButton from '../../../common/aur-button/AurButton';
import AurDrawer from '../../../common/aur-drawer/AurDrawer';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {IFieldModal} from './constant';
import CustomConfirmDialog, {
  CustomConfirmDialogType,
} from './CustomConfirmDialog';
import {ITag} from './DraggableTag';
import {
  FieldCategoryActivity,
  IFieldCategoryActivityProps,
} from './FieldCategoryActivity';
import {FieldCategoryList} from './FieldCategoryList';
import {FieldCategoryMetadataDetails} from './FieldCategoryMetadataDetails';
import {FieldCategoryTags} from './FieldCategoryTags';
import {
  FieldCategoryType,
  FieldValues,
  FieldView,
  IFieldViewProps,
} from './FieldView';
import {ModalPagination} from './ModalPagination';
import {useCustomField} from './useCustomField';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
export const regexValidationField = /^\w*$/;

type CustomFieldMode = 'CREATE' | 'VIEW' | 'EDIT';

interface IPagination {
  initialIndex: number;
  fieldsData?: IFieldModal[];
}

interface ICustomFieldDrawerProps
  extends Omit<
    IFieldViewProps,
    'headerTitle' | 'fieldFormTypes' | 'dismissError' | 'headerClass'
  > {
  open: boolean;
  headerClass?: string;
  headerTitle?: string;
  fieldMode?: CustomFieldMode;
  pagination?: IPagination;
  onClose?: (isRefreshData: boolean) => void;
}

const CustomFieldDrawer: React.FC<ICustomFieldDrawerProps> = ({
  open,
  fieldMode = 'VIEW',
  fieldCategoryType,
  headerTitle = t('fieldManagement.customField.customTagFieldHeader'),
  headerClass = 'typo-text display2_size',
  pagination,
  onClose,
  ...fieldViewProps
}) => {
  const [formMode, setFormMode] = useState<CustomFieldMode>(fieldMode);
  const [isModal, setIsModal] = useState<boolean>(open);
  const [unsavedChangesType, setUnsavedChangesType] = useState<
    CustomConfirmDialogType | undefined
  >();
  const [categoryType, setCategoryType] =
    useState<FieldCategoryType>(fieldCategoryType);
  const [currentIndex, setCurrentIndex] = useState<number>(
    pagination ? pagination.initialIndex : 0
  );
  const paginationDataRef = useRef<IPagination | undefined>(pagination);
  const [fieldActivity, setFieldActivity] = useState<
    IFieldCategoryActivityProps | undefined
  >();
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [tags, setTags] = useState<ITag[]>([]);
  const [isEditingOrder, setIsEditingOrder] = useState<boolean>(false);
  const [isChangedInTags, setIsChangedInTags] = useState<boolean>(false);

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editModeParamKey, setEditModeParamKey] = useState<string>('');
  const {hasCreateOrEditTagFieldsPerm, hasCreateOrEditCustomFieldsPerm} =
    usePermissionsByPage(PAGE_NAME.FIELD_AND_TAG_MANAGEMENT);
  const isRefreshed = useRef<boolean>(false);

  const {
    fieldRef,
    loading,
    duplicateError,
    duplicateTagError,
    usedTagError,
    createField,
    createMetaDataField,
    updateField,
    addTag,
    deleteTag,
    updateTag,
    clearDuplicateError,
    clearDuplicateTagError,
    clearUsedTagError,
  } = useCustomField();

  const setFieldDataInState = useCallback(() => {
    if (fieldRef?.current) {
      setFieldValues({
        fieldName: fieldRef.current.fieldLabel,
        fieldType: fieldRef.current.fieldUIType as never,
        isMultiValueField: fieldRef.current.isMultiValueField,
        isRelationShipField: fieldRef.current.isRelationShipField,
        isRelationalEditable: fieldRef.current?.isRelationalEditable ?? false,
      });
      setFieldActivity({
        createdBy: fieldRef.current.createdBy ?? '',
        createdOn: fieldRef.current.createdOn ?? '',
        updatedBy: fieldRef.current.updatedBy ?? '',
        updatedOn: fieldRef.current.updatedOn ?? '',
      });
      setTags(
        orderBy(fieldRef.current.tagValues ?? [], ['displayOrder'], ['desc'])
      );
      setCategoryType(fieldRef.current.fieldCategory);
    }
  }, [fieldRef]);

  const updateFieldData = useCallback(() => {
    if (paginationDataRef.current?.fieldsData && fieldRef?.current) {
      const fieldsData = paginationDataRef.current?.fieldsData.map((p) =>
        p.fieldId === fieldRef.current?.fieldId ? fieldRef.current : p
      );
      paginationDataRef.current = {
        ...paginationDataRef.current,
        fieldsData,
      };
    } else {
      setFieldDataInState();
    }
  }, [fieldRef, setFieldDataInState]);

  useEffect(() => {
    if (paginationDataRef.current?.fieldsData) {
      const fieldData = paginationDataRef.current.fieldsData[currentIndex];
      fieldRef.current = fieldData;
      setFieldDataInState();
    }
  }, [
    currentIndex,
    paginationDataRef.current?.fieldsData,
    fieldRef,
    setFieldDataInState,
  ]);

  const isRenameButtonHidden = useMemo(() => {
    if (paginationDataRef.current?.fieldsData) {
      const fieldData = paginationDataRef.current.fieldsData[currentIndex];
      return fieldData.isDocCustom ?? false;
    }
    return false;
  }, [paginationDataRef.current?.fieldsData, currentIndex]);

  const categoryTypes = useMemo(() => {
    return {
      isTag: categoryType?.toUpperCase() === 'TAG_FIELD',
      isCustomMeta: categoryType?.toUpperCase() === 'CUSTOM_METADATA',
      isSTDMeta: categoryType?.toUpperCase() === 'STANDARD_METADATA',
      isExport: categoryType?.toUpperCase() === 'EXPORT',
      isSTR: categoryType?.toUpperCase() === 'STR',
    };
  }, [categoryType]);

  const canRenameField = useMemo(() => {
    if (categoryTypes.isTag) return hasCreateOrEditTagFieldsPerm;
    if (categoryTypes.isCustomMeta) return hasCreateOrEditCustomFieldsPerm;

    return false;
  }, [
    categoryTypes.isTag,
    categoryTypes.isCustomMeta,
    hasCreateOrEditTagFieldsPerm,
    hasCreateOrEditCustomFieldsPerm,
  ]);

  const shouldHideRenameButton = useMemo(() => {
    return isRenameButtonHidden || !canRenameField;
  }, [isRenameButtonHidden, canRenameField]);

  const subTitleByCategoryType = useMemo(() => {
    if (categoryTypes.isCustomMeta) {
      return t('fieldManagement.customField.customMetadataField');
    } else if (categoryTypes.isSTDMeta) {
      return t('fieldManagement.customField.standardMetadataField');
    } else if (categoryTypes.isExport) {
      return t('fieldManagement.customField.exportField');
    } else if (categoryTypes.isSTR) {
      return t('fieldManagement.customField.strField');
    } else {
      return t('fieldManagement.customField.customTagField');
    }
  }, [
    categoryTypes.isCustomMeta,
    categoryTypes.isExport,
    categoryTypes.isSTDMeta,
    categoryTypes.isSTR,
  ]);

  const formTypes = useMemo(() => {
    return {
      isCreate: formMode.toUpperCase() === 'CREATE',
      isEditForm: formMode.toUpperCase() === 'EDIT',
      isView: formMode.toUpperCase() === 'VIEW',
      isViewOnly:
        (formMode.toUpperCase() === 'VIEW' && categoryTypes.isSTR) ||
        (formMode.toUpperCase() === 'VIEW' && categoryTypes.isExport) ||
        (formMode.toUpperCase() === 'VIEW' && categoryTypes.isSTDMeta),
    };
  }, [
    categoryTypes.isExport,
    categoryTypes.isSTDMeta,
    categoryTypes.isSTR,
    formMode,
  ]);

  const handleUnsavedDialog = useCallback((type?: CustomConfirmDialogType) => {
    setUnsavedChangesType(type);
  }, []);

  const dismissModal = useCallback(() => {
    setIsModal(false);
    onClose?.(isRefreshed.current);
  }, [onClose]);

  const onCloseClicked = useCallback(() => {
    if (isChangedInTags) {
      handleUnsavedDialog('close');
    } else {
      dismissModal();
    }
  }, [dismissModal, isChangedInTags, handleUnsavedDialog]);

  const handlePreviousClick = useCallback(() => {
    setCurrentIndex(Math.max(0, currentIndex - 1));
    setFormMode('VIEW');
  }, [currentIndex]);

  const handleNextClick = useCallback(() => {
    setCurrentIndex(
      Math.min(
        currentIndex + 1,
        paginationDataRef.current?.fieldsData?.length ?? 0
      )
    );
    setFormMode('VIEW');
  }, [currentIndex]);

  const onUnsavedCancel = useCallback(() => {
    handleUnsavedDialog();
  }, [handleUnsavedDialog]);

  const onUnsavedOk = useCallback(() => {
    handleUnsavedDialog();
    dismissModal();
  }, [dismissModal, handleUnsavedDialog]);

  const setViewForm = useCallback(
    (response: boolean) => {
      if (response) {
        updateFieldData();
        setFieldDataInState();
        setFormMode('VIEW');
      }
    },
    [setFieldDataInState, updateFieldData]
  );

  const onSave = useCallback(
    (updateFieldValues: FieldValues) => {
      isRefreshed.current = true;
      if (formTypes.isCreate && categoryTypes.isTag) {
        createField(updateFieldValues.fieldName ?? '')
          .then(setViewForm)
          .catch(noop);
      } else if (formTypes.isCreate && categoryTypes.isCustomMeta) {
        createMetaDataField(updateFieldValues)
          .then(() => {
            dismissModal();
          })
          .catch(noop);
      } else if (formTypes.isEditForm) {
        updateField({
          label: updateFieldValues.fieldName ?? '',
          isRelationShipField: fieldRef?.current?.isRelationShipField ?? false,
        })
          .then(setViewForm)
          .catch(noop);
      }
    },
    [
      categoryTypes.isCustomMeta,
      categoryTypes.isTag,
      createField,
      createMetaDataField,
      dismissModal,
      formTypes.isCreate,
      formTypes.isEditForm,
      setViewForm,
      updateField,
      fieldRef,
    ]
  );

  const handleFieldCancel = useCallback(() => {
    if (formTypes.isEditForm) {
      setFormMode('VIEW');
    } else {
      dismissModal();
    }
  }, [dismissModal, formTypes.isEditForm]);

  const onRename = useCallback((updateFieldValues: FieldValues) => {
    setFormMode('EDIT');
    setFieldValues(updateFieldValues);
  }, []);

  const handleSaveChanges = useCallback(
    (updatedTags: ITag[]) => {
      isRefreshed.current = true;
      const reorderTags = updatedTags.map((tag, index) => {
        return {...tag, displayOrder: updatedTags.length - index};
      });
      updateTag(reorderTags).then(setViewForm).catch(noop);
    },
    [setViewForm, updateTag]
  );

  const handleAddTag = useCallback(
    (value: string) => {
      isRefreshed.current = true;
      addTag(value, tags.length + 1)
        .then(setViewForm)
        .catch(noop);
    },
    [addTag, setViewForm, tags.length]
  );

  const handlEditTag = useCallback(
    (tag: ITag): Promise<boolean> => {
      return new Promise((resolve) => {
        isRefreshed.current = true;
        updateTag([tag])
          .then((value) => {
            setViewForm(value);
            resolve(value);
          })
          .catch(noop);
      });
    },
    [setViewForm, updateTag]
  );

  const handleDeleteTag = useCallback(
    (tagId: number) => {
      isRefreshed.current = true;
      deleteTag(tagId).then(setViewForm).catch(noop);
    },
    [deleteTag, setViewForm]
  );

  const onChangeEditMode = useCallback(
    (paramKey: string, isEditMode: boolean) => {
      setEditModeParamKey(isEditMode ? paramKey : '');
      setIsEditMode(isEditMode);
    },
    []
  );

  const onSaveKey = useCallback(
    (paramKey: string, value = '') => {
      let paramBoolValue = false;
      let paramObj: Record<string, string | boolean> = {};

      if (paramKey === 'isRelationShipField') {
        if (
          value.toLowerCase() ===
          t('fieldManagement.customField.yes').toLowerCase()
        ) {
          paramBoolValue = true;
        }

        paramObj = {
          ...paramObj,
          isRelationShipField: paramBoolValue,
          label: fieldRef.current?.fieldLabel ?? '',
          name: fieldRef.current?.fieldName ?? '',
        };
      }

      updateField(paramObj, t('fieldManagement.customField.fieldUpdated'))
        .then((response) => {
          setViewForm(response);
          isRefreshed.current = true;
        })
        .catch(noop);
    },
    [fieldRef, setViewForm, updateField]
  );

  const renderItemByCategory = useMemo(() => {
    if (categoryTypes.isTag) {
      return (
        <FieldCategoryTags
          onOrderChanges={setIsChangedInTags}
          tags={tags}
          showTagError={duplicateTagError}
          dismissTagError={clearDuplicateTagError}
          usedTagError={usedTagError}
          dismissUsedError={clearUsedTagError}
          onSaveChanges={handleSaveChanges}
          addTag={handleAddTag}
          deleteTag={handleDeleteTag}
          editTag={handlEditTag}
          onToggleReorderChanges={setIsEditingOrder}
          canEditTags={hasCreateOrEditTagFieldsPerm}
        />
      );
    } else if (categoryTypes.isCustomMeta || categoryTypes.isSTDMeta) {
      return (
        <FieldCategoryMetadataDetails
          fieldData={fieldRef?.current}
          editModeParamKey={editModeParamKey}
          disabled={isEditMode}
          onSaveKey={onSaveKey}
          onChangeEditMode={onChangeEditMode}
          canEditCustomFields={hasCreateOrEditCustomFieldsPerm}
        />
      );
    } else if (categoryTypes.isExport || categoryTypes.isSTR) {
      return <FieldCategoryList fieldData={fieldRef?.current} />;
    }
  }, [
    categoryTypes.isTag,
    categoryTypes.isCustomMeta,
    categoryTypes.isSTDMeta,
    categoryTypes.isExport,
    categoryTypes.isSTR,
    tags,
    duplicateTagError,
    clearDuplicateTagError,
    usedTagError,
    clearUsedTagError,
    handleSaveChanges,
    handleAddTag,
    handleDeleteTag,
    handlEditTag,
    hasCreateOrEditTagFieldsPerm,
    fieldRef,
    editModeParamKey,
    isEditMode,
    onSaveKey,
    onChangeEditMode,
    hasCreateOrEditCustomFieldsPerm,
  ]);

  return (
    <>
      <AurDrawer
        className="client-drawer"
        open={isModal}
        onClose={onCloseClicked}
        anchor="right"
        drawerHeader={
          <Box className="custom-field-drawer-header-master">
            <Box className="custom-field-drawer-header">
              <AurButton
                classNames="drawer-close-button"
                label={t('common.close')}
                type="exit"
                size="Medium"
                title={t('common.close')}
                onClick={onCloseClicked}
                ariaLabel={t('fieldManagement.customField.closeButtonHint')}
              />
              {paginationDataRef.current?.fieldsData &&
                !formTypes.isEditForm &&
                paginationDataRef.current.fieldsData.length !== 0 && (
                  <ModalPagination
                    disabled={isEditingOrder || isEditMode}
                    currentIndex={currentIndex}
                    maxIndex={paginationDataRef.current.fieldsData.length}
                    onPrevious={handlePreviousClick}
                    onNext={handleNextClick}
                    nextButtonAriaLabel={t(
                      'fieldManagement.customField.pagination.nextButtonHint'
                    )}
                    previousButtonAriaLabel={t(
                      'fieldManagement.customField.pagination.previousButtonHint'
                    )}
                  />
                )}
            </Box>
          </Box>
        }
        drawerContent={
          <AurDrawerPanelContainer
            contentHeader={
              <FieldView
                fieldFormTypes={formTypes}
                fieldCategoryType={categoryType}
                values={fieldValues}
                headerTitle={headerTitle}
                headerSubtitle={subTitleByCategoryType}
                showError={duplicateError}
                headerClass={headerClass}
                dismissError={clearDuplicateError}
                onCancel={handleFieldCancel}
                onSave={onSave}
                onRename={onRename}
                isEditingOrder={isEditingOrder || isEditMode}
                renameButtonHidden={shouldHideRenameButton}
                {...fieldViewProps}
              />
            }
            contentBody={
              !formTypes.isCreate && (
                <Box
                  className={'custom-field-drawer-body'}
                  sx={
                    formTypes.isEditForm
                      ? {
                          pointerEvents: 'none',
                          userSelect: 'none',
                        }
                      : undefined
                  }
                >
                  {renderItemByCategory}
                  {fieldActivity && (
                    <FieldCategoryActivity
                      editModeParamKey={editModeParamKey}
                      disabled={isEditMode}
                      createdOn={fieldActivity.createdOn}
                      createdBy={fieldActivity.createdBy}
                      updatedOn={
                        categoryTypes.isSTR
                          ? undefined
                          : fieldActivity.updatedOn
                      }
                      updatedBy={
                        categoryTypes.isSTR
                          ? undefined
                          : fieldActivity.updatedBy
                      }
                    />
                  )}
                  {formTypes.isEditForm && (
                    <Box className={'custom-field-edit-overlay'}></Box>
                  )}
                </Box>
              )
            }
          />
        }
      />
      {unsavedChangesType && (
        <CustomConfirmDialog
          confirmType={unsavedChangesType}
          showDialog={true}
          onOk={onUnsavedOk}
          onCancel={onUnsavedCancel}
        />
      )}
      {loading && <AurLoadingIndicator overlay={true} isOpen={loading} />}
    </>
  );
};

export {CustomFieldDrawer};
